import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";

const languages = {
  en: {
    nativeName: "English",
    flag: "🇺🇸",
  },
  id: {
    nativeName: "Bahasa Indonesia",
    flag: "🇮🇩",
  },
};

// Globe icon SVG
const GlobeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// Chevron down icon
const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s ease",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.resolvedLanguage || "en");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectLanguage = (langCode: string) => {
    setCurrentLang(langCode);
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  const currentLanguage =
    languages[currentLang as keyof typeof languages] || languages.en;

  return (
    <Box position="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Box
        as="button"
        display="flex"
        alignItems="center"
        gap="1.5"
        px="2"
        py="1"
        bg="transparent"
        color="fg"
        borderRadius="full"
        cursor="pointer"
        transition="all 0.2s ease"
        _hover={{
          bg: "bg.subtle",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Box color="fg.muted">
          <GlobeIcon />
        </Box>
        <Text fontSize="xs" fontWeight="medium" whiteSpace="nowrap">
          {currentLanguage.nativeName}
        </Text>
        <Box color="fg.muted">
          <ChevronIcon isOpen={isOpen} />
        </Box>
      </Box>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          right="0"
          mt="1"
          bg={{ base: "white", _dark: "rgba(15, 23, 42, 0.95)" }}
          borderRadius="lg"
          boxShadow={{ base: "lg", _dark: "0 8px 32px rgba(0,0,0,0.6)" }}
          border="1px solid"
          borderColor={{ base: "gray.100", _dark: "whiteAlpha.200" }}
          backdropFilter={{ _dark: "blur(16px)" }}
          overflow="hidden"
          zIndex="dropdown"
          animation="fadeIn 0.15s ease-out"
          css={{
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(-4px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {Object.entries(languages).map(([code, lang]) => (
            <Box
              key={code}
              as="button"
              w="full"
              display="flex"
              alignItems="center"
              gap="2"
              px="3"
              py="2"
              bg={currentLang === code ? "accent.muted" : "transparent"}
              cursor="pointer"
              transition="all 0.15s ease"
              whiteSpace="nowrap"
              _hover={{
                bg: currentLang === code ? "accent.muted" : "bg.subtle",
              }}
              onClick={() => handleSelectLanguage(code)}
            >
              <Text fontSize="sm">{lang.flag}</Text>
              <Text
                fontSize="xs"
                fontWeight={currentLang === code ? "semibold" : "normal"}
                color="fg"
                whiteSpace="nowrap"
              >
                {lang.nativeName}
              </Text>
              {currentLang === code && (
                <Box ml="auto" color="accent" pl="2">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default LanguageSwitcher;
